import { useState, useEffect } from 'react';
import { API } from '../../../../Common/lib/api';
import { HiOutlineInformationCircle } from "react-icons/hi";
import { Popover } from '@headlessui/react'

import RequestVerificationModal from '../../Modals/requestVerificationModal';

export default function VerificationSection({ userProfile }) {
  const [openVerificationModal, setOpenVerificationModal] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState("verified");

  const [verificationRequests, setVerificationRequests] = useState([""]);

  // Updates verification status when user's profile info loads or changes
  useEffect(() => {
    getVerificationStatus();
  }, [userProfile, verificationRequests]);

  // Call checkExistingRequests once userProfile state is updated since it requires user data
  useEffect(() => {
    checkExistingRequests();
  }, [userProfile]);

  async function checkExistingRequests() {
    try {
      // Checks if userProfile state is populated
      if (userProfile.username !== "") {
        const params = {
          category: "VerificationRequest",
          userID: userProfile.username,
        };
        
        const verificationsQuery = await API.logs.list(params);
        const items = verificationsQuery.data?.listLogs?.items || verificationsQuery.data?.items || [];
        setVerificationRequests(items.filter(administration => administration.category === "VerificationRequest"));
      }
    } catch (e) {
      console.error('Error checking verification requests:', e);
      setVerificationRequests([]);
    }
  }

  function getVerificationStatus() {
    // Staff or approved contributors are considered verified
    const isContributor = userProfile.contributor_access === true || userProfile.contributor_access === "true";
    if (userProfile.is_staff || isContributor) {
      setVerificationStatus("verified");
      return;
    }

    // Pending request
    if (verificationRequests.length !== 0) {
      setVerificationStatus("under review");
      return;
    }

    setVerificationStatus("unverified");
  }

  function getStatusPill() {
    const isContributor = userProfile.contributor_access === true || userProfile.contributor_access === "true";

    if ((verificationRequests.length !== 0) && !userProfile.is_staff && !isContributor) {
      return (
        <span className="whitespace-nowrap inline-flex items-center h-6 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-300 dark:text-yellow-900">
          Under Review
        </span>
      )
    }
    if (userProfile.is_staff || isContributor) {
      return (
        <span className="whitespace-nowrap inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700 dark:bg-green-300 dark:text-green-900">
          Verified
        </span>
      )
    }
    return (
      <span className="whitespace-nowrap inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700 dark:bg-red-300 dark:text-red-900">
        Unverified
      </span>
    )
  }

   /**
   * Checks the user's edit_locked attribute and returns a pill shaped badge icon to display their edit permissions.
   * @returns Badge Component
   */
   function getEditPill() {

    if (userProfile.edit_locked === "true") {
      return (
        <span className="whitespace-nowrap inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700 dark:bg-red-300 dark:text-red-900">
          Disabled
        </span>
      )
    }
    else {
      return (
        <span className="whitespace-nowrap inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700 dark:bg-green-300 dark:text-green-900">
          Enabled
        </span>
      )
    }
  }

  return (
    <>
      <div className="bg-gray-50 rounded-xl border border-gray-200 shadow-sm p-8 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">Contributor Status</h2>
        </div>

        <dl className="mt-2 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 dark:divide-gray-700 dark:border-gray-700">
          <div className="pt-6 sm:flex items-center">
            <dt className="flex font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 dark:text-white">Contributor Status
              <Popover className="ml-2 items-center flex ">
                <Popover.Button className="focus:outline-none"><HiOutlineInformationCircle className="h-4 w-4 text-gray-400 cursor-pointer"/><span className="sr-only">More information on contributor status</span></Popover.Button>

                <Popover.Panel className="absolute mt-16 z-10">
                  <div className="bg-gray-900 py-1 px-2 opacity-80 rounded">
                    <p className="text-xs text-white">Contributor status determines your eligibility to upload datasets. <br/>Only verified contributors can upload datasets.</p>
                  </div>
                </Popover.Panel>
              </Popover>
            </dt>
            <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
              <div className="text-gray-900">{getStatusPill()}</div>
            </dd>
          </div>
          { verificationStatus === "verified" &&
            <div className="pt-6 sm:flex items-center">
              <dt className="flex font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 dark:text-white">Edit Permissions
                <Popover className="ml-2 items-center flex ">
                  <Popover.Button className="focus:outline-none"><HiOutlineInformationCircle className="h-4 w-4 text-gray-400 cursor-pointer"/><span className="sr-only">More information on project editing permissions</span></Popover.Button>

                  <Popover.Panel className="absolute mt-16 z-10">
                    <div className="bg-gray-900 py-1 px-2 opacity-80 rounded">
                      <p className="text-xs text-white">Edit permissions determine your ability to edit your uploaded datasets. <br/>This is on by default.</p>
                    </div>
                  </Popover.Panel>
                </Popover>
              </dt>
              <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                <div className="text-gray-900">{getEditPill()}</div>
              </dd>
            </div>
          }
          {/* If the user is not already verified, gives them an option to apply */}
          {verificationStatus === "unverified" &&
            <div className="pt-6 sm:flex items-center">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-400">Want to submit datasets? <a onClick={() => setOpenVerificationModal(true)} className="font-medium text-blue-700 dark:text-blue-500">Request verification <span aria-hidden="true"> &rarr;</span> </a></p>
            </div>
          }


        </dl>
      </div>
      <RequestVerificationModal setOpenVerificationModal={setOpenVerificationModal} openVerificationModal={openVerificationModal} userProfile={userProfile} checkExistingRequests={checkExistingRequests} />
    </>
  );
}